import React, { useEffect, useState } from 'react';
import parse, { DOMNode } from 'html-react-parser';
import { Box, Button, colors, Stack, Typography } from '@mui/material';

import { translate as t } from 'hooks/useTranslations';
import Modal from 'components/_layouts/Modal';
import { PrefaceComparison } from './preface_comparison';
import prefaceComparer from './comparer';
import { DraftPreface } from 'views/App/Portal/Policy/_types/manuals/draft';
import contentConversionHelper from '../../../helpers/contentConversionHelper';
import PolicyContentHelper from 'views/App/Portal/Policy/_helpers/policyContentHelper';

type PrefaceCompareProps = {
  committedPrefaceQuery: any;
  currentPreface: DraftPreface;
  id: string;
  isOpenComparePreface: boolean;
  revertChanges: () => void;
  setIsOpenComparePreface: React.Dispatch<React.SetStateAction<boolean>>;
};

const PrefaceCompare: React.FC<PrefaceCompareProps> = ({
  committedPrefaceQuery,
  currentPreface,
  id,
  isOpenComparePreface,
  revertChanges,
  setIsOpenComparePreface,
}) => {
  const [comparison, setComparison] = useState<PrefaceComparison | null>(null);

  const handleAcceptChanges = () => {
    localStorage.removeItem(`preface-lock_${id}`);
    setIsOpenComparePreface(false);
  };

  const handleRevertChanges = async () => {
    await revertChanges();
    localStorage.removeItem(`preface-lock_${id}`);
    setIsOpenComparePreface(false);
  };

  useEffect(() => {
    if (committedPrefaceQuery.data?.title && currentPreface?.title && isOpenComparePreface) {
      setComparison(prefaceComparer.compare(committedPrefaceQuery.data, currentPreface));
    }
  }, [committedPrefaceQuery.data, isOpenComparePreface, currentPreface]);

  return (
    <Modal
      title={t('draftManuals.prefaceCompare.title')}
      size='lg'
      open={isOpenComparePreface}
      onClose={() => handleAcceptChanges()}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          columnGap: 4,
        }}
      >
        <Typography variant='subtitle1'>{t('draftManuals.prefaceCompare.versions.committed')}</Typography>
        <Typography variant='subtitle1'>{t('draftManuals.prefaceCompare.versions.staged')}</Typography>
        <Box
          sx={{
            height: '50vh',
            overflow: 'auto',
            border: `solid 1px ${colors.grey[200]}`,
            p: 4,
            '& del': {
              textDecoration: 'none',
              color: '#b30000',
              background: colors.red[100],
            },
            '& ins': {
              textDecoration: 'none',
              color: '#406619',
              background: colors.green[100],
              visibility: 'hidden',
              display: 'none',
            },
            '& p>span>ins': {
              display: 'block',
            },
          }}
        >
          {comparison && (
            <Stack spacing={3}>
              <Typography
                component='div'
                variant='h5'
              >
                {parse(comparison.title)}
              </Typography>
              <Box sx={{ minHeight: 0, overflow: 'auto' }}>
                {parse(contentConversionHelper.contentForRender(comparison.content ?? ''), {
                  replace(node: DOMNode, ind: number) {
                    return PolicyContentHelper.handleContent(node, ind);
                  },
                })}
              </Box>
            </Stack>
          )}
        </Box>
        <Box
          sx={{
            height: '50vh',
            overflow: 'auto',
            border: `solid 1px ${colors.grey[200]}`,
            p: 4,
            '& del': {
              textDecoration: 'none',
              color: '#b30000',
              background: colors.red[100],
              visibility: 'hidden',
              display: 'none',
            },
            '& p>span>del': {
              display: 'block',
            },
            '& ins': {
              textDecoration: 'none',
              color: '#406619',
              background: colors.green[100],
            },
          }}
        >
          {comparison && (
            <Stack spacing={3}>
              <Typography
                component='div'
                variant='h5'
              >
                {parse(comparison.title)}
              </Typography>
              <Box sx={{ minHeight: 0, overflow: 'auto' }}>
                {parse(contentConversionHelper.contentForRender(comparison.content ?? ''), {
                  replace(node: DOMNode, ind: number) {
                    return PolicyContentHelper.handleContent(node, ind);
                  },
                })}
              </Box>
            </Stack>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 4,
          }}
        >
          <Button
            color='error'
            onClick={handleRevertChanges}
          >
            {t('draftManuals.prefaceCompare.actions.reject')}
          </Button>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 4,
          }}
        >
          <Button
            variant='contained'
            color='success'
            onClick={handleAcceptChanges}
          >
            {t('draftManuals.prefaceCompare.actions.accept')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default PrefaceCompare;
